import { useQuery, useQueryClient } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError, HTTPErrors } from "../../errors";
import KarteInterface, {
  KarteCommentInterface,
} from "../../interfaces/KarteInterface";

const buildKey = (studentId: string, karteId: string) => [
  "/api/v1/students/{student_id}/kartes/{karte_hashid}",
  studentId,
  karteId,
];

// 特定のカルテを取得する
export const useFetchKarte = ({
  studentId,
  karteId,
}: {
  studentId: string;
  karteId: string;
}) => {
  return useQuery<KarteInterface, HTTPErrors>({
    queryKey: buildKey(studentId, karteId),
    queryFn: async () => {
      const { data, response } = await boronClient.GET(
        "/api/v1/students/{student_id}/kartes/{karte_hashid}",
        {
          params: {
            path: {
              student_id: studentId,
              karte_hashid: karteId,
            },
          },
        },
      );

      if (response.ok && data) {
        return data.karte;
      }
      throw await createError(response);
    },
  });
};

export const useUpdateKarteCache = ({
  studentId,
  karteId,
}: {
  studentId: string;
  karteId: string;
}) => {
  const queryClient = useQueryClient();

  const updateKarteCache = (karte: KarteInterface) => {
    queryClient.setQueryData<KarteInterface>(
      buildKey(studentId, karteId),
      karte,
    );
  };

  return {
    updateKarteCache,
  };
};

export const useAddKarteCommentCache = ({
  studentId,
  karteId,
}: {
  studentId: string;
  karteId: string;
}) => {
  const queryClient = useQueryClient();

  const addKarteCommentCache = (newComment: KarteCommentInterface) => {
    queryClient.setQueryData<KarteInterface>(
      buildKey(studentId, karteId),
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          comments: [...oldData.comments, newComment],
        };
      },
    );
  };

  return {
    addKarteCommentCache,
  };
};

export const useRemoveKarteCommentCache = ({
  studentId,
  karteId,
}: {
  studentId: string;
  karteId: string;
}) => {
  const queryClient = useQueryClient();

  const removeKarteCommentCache = (commentId: string) => {
    queryClient.setQueryData<KarteInterface>(
      buildKey(studentId, karteId),
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          comments: oldData.comments.filter(
            (comment) => comment.id !== commentId,
          ),
        };
      },
    );
  };

  return {
    removeKarteCommentCache,
  };
};
